import { Vue, Component, Prop, Watch, Inject } from 'vue-property-decorator'
import { cloneDeep } from 'lodash'
import {
    CustomConfigurationModal, initialCustomConfigurationFormState
} from '@/modules/configurationModule/detail/notifications/components/alarms-modal-custom-configuration/alarms-modal-custom-configuration.model'
import { AlarmType, CustomConfiguration } from '@/modules/configurationModule/detail/notifications/models/alarms'
import PersonnelConfigurationServices
    from '@/api/resources/configuration/peopleManagement/personnel/PersonnelConfigurationServices'
import { personnelService } from '@/api/resources/personnelService'
import AlarmsTypes from '@/modules/configurationModule/detail/notifications/utils/alarmsTypes'
import { alarmNotificationTypeWithCustomConfiguration } from '@/modules/configurationModule/detail/notifications/components/alarms-modal/alarms-modal.models'
import { Nullable } from '@/shared/types/app.types'

@Component
export default class AlarmsModalCustomConfiguration extends Vue {
    @Prop() readonly laboratoryId!: number
    @Prop() readonly alarmType!: AlarmType
    @Prop() readonly alarmCustomConfiguration!: CustomConfiguration
    @Inject() externalValidator!: any
    @Watch('alarmType', { deep: true })
    async onAlarmTypeChanged() {
        if (this.alarmType) {
            await this.initializeCustomConfiguration()
        }
    }

    private state: CustomConfigurationModal
    private alarmTypes!: AlarmsTypes
    private selectedDay!: Nullable<string>
    private daysToTargetDateListChanged!: boolean

    constructor() {
        super()
        this.state = initialCustomConfigurationFormState()
        this.alarmTypes = new AlarmsTypes(null)
        this.selectedDay = null
        this.$validator = this.externalValidator
    }
    async mounted() {
        if (this.alarmType) {
            await this.initializeCustomConfiguration()
        }
    }

    async initializeCustomConfiguration() {
        this.alarmTypes = new AlarmsTypes(this.alarmType.code)
        if (this.alarmCustomConfiguration.receiverPersonnelIds?.length) {
            await this.getReceiverPersonnelList(this.alarmCustomConfiguration.receiverPersonnelIds)
        }
        const daysToTargetDateList = this.getDaysToTargetList(this.alarmType, this.alarmCustomConfiguration)
        const daysToDelay = this.getDaysToDelay(this.alarmType, this.alarmCustomConfiguration)
        this.state.customConfiguration = cloneDeep({
            receiverPersonnelList: this.alarmCustomConfiguration.receiverPersonnelList,
            daysToTargetDateList: daysToTargetDateList,
            daysToDelay: daysToDelay,
            shouldNotifyPhysicianPetitioner: this.alarmCustomConfiguration.shouldNotifyPhysicianPetitioner,
            shouldNotifyPhysicianDepartment: this.alarmCustomConfiguration.shouldNotifyPhysicianDepartment
        })
        this.daysToTargetDateListChanged = false
    }
    async getReceiverPersonnelList(receiverPersonnelIds: Array<number>): Promise<any> {
        const promises = receiverPersonnelIds?.map(async (personnelId) => PersonnelConfigurationServices.getPersonnelById(personnelId))
        Promise.all(promises).then((personnelList) => {
            this.state.customConfiguration.receiverPersonnelList = personnelList
        })
    }
    onAddDayClick() {
        if (this.selectedDay) {
            this.state.customConfiguration.daysToTargetDateList?.push(this.selectedDay)
        }
    }

    onDeleteDayClick(day: string) {
        this.daysToTargetDateListChanged = !this.daysToTargetDateListChanged
        this.state.customConfiguration.daysToTargetDateList = this.state.customConfiguration.daysToTargetDateList?.filter((targetDay: string) => targetDay !== day)
    }

    get addDayButtonDisabled(): boolean {
        return !!this.state.customConfiguration.daysToTargetDateList?.find((targetDay: string) => Number(targetDay) === Number(this.selectedDay))
    }

    get showSampleDiscardDelayReminderAndRequestUnlockDelayFields(): boolean {
        return this.alarmTypes.sampleDiscardDelayReminderAlarmType || this.alarmTypes.requestUnlockDelayAlarmType
    }

    get showSampleShelftGrossingAndRequestRegisterDelayFields(): boolean {
        return this.alarmTypes.sampleShelfDelayAlarmType || this.alarmTypes.sampleGrossingDelayAlarmType || this.alarmTypes.requestRegisterDelayAlarmType
    }

    get showCaseProximityToGlobalAndDiagnosticTargetDateFields(): boolean {
        return this.alarmTypes.caseProximityToGlobalTargetDateAlarmType || this.alarmTypes.caseProximityToDiagnosticTargetDateAlarmType
    }

    get showRequestAndComplementaryApReportVisualizationDelayFields(): boolean {
        return this.alarmTypes.requestApReportVisualizationDelayAlarmType || this.alarmTypes.complementaryApReportVisualizationDelayAlarmType
    }

    getDaysToDelay(alarmType: AlarmType, customConfiguration: CustomConfiguration) {
        switch (alarmType.code) {
        case alarmNotificationTypeWithCustomConfiguration.SAMPLE_DISCARD_DELAY_REMINDER:
            return customConfiguration.daysToRemindSampleDiscardDelayed
        case alarmNotificationTypeWithCustomConfiguration.REQUEST_UNLOCK_DELAY:
            return customConfiguration.daysBeforeUnlockDelay
        default:
            return customConfiguration.daysToDelay
        }
    }

    getDaysToTargetList(alarmType: AlarmType, customConfiguration: CustomConfiguration) {
        switch (alarmType.code) {
        case alarmNotificationTypeWithCustomConfiguration.CASE_PROXIMITY_TO_GLOBAL_TARGET_DATE:
            return customConfiguration.daysToGlobalTargetDateList
        case alarmNotificationTypeWithCustomConfiguration.CASE_PROXIMITY_TO_DIAGNOSTIC_TARGET_DATE:
            return customConfiguration.daysToDiagnosticTargetDateList
        }
    }

    get showEmptyState(): boolean {
        return this.$_isEmpty(this.alarmType)
    }

    async getPersonnel() {
        return personnelService.getPersonnel(this.laboratoryId, null)
    }
}
